import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Selector } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';

import styles from './Selectors.css';
import { getMapLayers } from '../../config/mapLayers';
import { getMapLayerForSeason } from '../../utils/selectorOptions';
import { logDmbError } from '../../utils/error';
import { switchView } from '../../utils/url';
import { trackEvent, trackHeapEvent } from '../../actions/analyticsActions';
import { useAppSelector } from '../../utils/useAppSelector';
import { FeatureFlagKey, FHA_KEY } from '../../types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

const Selectors = (props) => {
  const {
    activeSeason,
    activeMapLayer,
    disabledMapLayers,
    layers,
    seasons,
    sortedSeasonKeys,
    isSplitViewEnabled,
    isSplitViewButtonVisible,
    activeSeasons,
    index,
    fieldId,
    trackAnalyticsEvent,
    trackAnalyticsHeapEvent
  } = props;


  const fieldAssets = useAppSelector(state => state.field.features);
  const hideSpatialDisplayFeatureLDFlag = useFeatureFlag(FeatureFlagKey.DMB_HIDE_SPATIAL_DISPLAY_FEATURE);

  useEffect(() => {
    if (layers.activeMapLayers.some(layer => layer.id === FHA_KEY)) trackAnalyticsHeapEvent('View FHA Image');
  }, [layers.activeMapLayers, trackAnalyticsHeapEvent])

  const updateSelectorVals = useCallback((layerId, seasonId) => {
    const seasonIds = activeSeasons.map((season) => season.id);
    const layerIds = layers.activeMapLayers.map((layer) => layer.id);
    switchView(index, seasonId, seasonIds, layerId, layerIds, fieldId);
  }, [index, fieldId, activeSeasons, layers.activeMapLayers]);

  const setSeason = useCallback((_, id) => {
    try {
      const mapLayer = getMapLayerForSeason(layers.list, activeMapLayer.id, id);
      updateSelectorVals(mapLayer.id, id);

      trackAnalyticsEvent('season', 'selected');
    } catch (error) {
      logDmbError(error, {
        appLocation: 'season selector',
        seasonId: id,
      });
    }
  }, [layers.list, activeMapLayer.id, updateSelectorVals, trackAnalyticsEvent]);

  const setMap = useCallback((_, id) => {
    try {
      updateSelectorVals(id, activeSeason.id);
      trackAnalyticsEvent('map', 'selected');
    } catch (error) {
      logDmbError(error, {
        appLocation: 'map layer selector',
        mapLayerId: id,
      });
    }
  }, [updateSelectorVals, activeSeason.id, trackAnalyticsEvent]);

  const seasonOptions = useMemo(() => sortedSeasonKeys.map((key) => ({
    name: seasons[key].name,
    id: key,
  })), [sortedSeasonKeys, seasons]);

  const mapOptions = useMemo(() => getMapLayers(
    activeSeason.id,
    layers.list,
    disabledMapLayers,
    fieldAssets,
    hideSpatialDisplayFeatureLDFlag
  ), [activeSeason.id, layers.list, disabledMapLayers, fieldAssets, hideSpatialDisplayFeatureLDFlag]);

  const className = `${styles.selectorsContainer} ${isSplitViewButtonVisible ? styles.selectorsContainerSpaced : ''
    }
     ${isSplitViewEnabled ? styles.selectorsContainerSplit : ''}`;

  return (
    <div className={className} data-test-id="dmb-selectors-container">
      <Selector
        className={styles.selectorSeason}
        name={getMessage('season')}
        options={seasonOptions}
        setValue={setSeason}
        testId="dmb-season-selector"
        value={activeSeason.id}
        valueName={activeSeason.name}
      />
      <Selector
        className={styles.selector}
        name={getMessage('maps')}
        options={mapOptions}
        setValue={setMap}
        testId="dmb-map-layer-selector"
        value={activeMapLayer.id}
        valueName={activeMapLayer.name}
      />
    </div>
  );
};

Selectors.propTypes = {
  activeMapLayer: PropTypes.object.isRequired,
  activeSeason: PropTypes.object.isRequired,
  activeSeasons: PropTypes.array.isRequired,
  disabledMapLayers: PropTypes.array.isRequired,
  fieldId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isSplitViewButtonVisible: PropTypes.bool,
  isSplitViewEnabled: PropTypes.bool,
  layers: PropTypes.object.isRequired,
  seasons: PropTypes.object.isRequired,
  sortedSeasonKeys: PropTypes.array.isRequired,
  trackAnalyticsEvent: PropTypes.func.isRequired,
  trackAnalyticsHeapEvent: PropTypes.func.isRequired,
};

function mapStateToProps(state, prop) {
  const { layers, field } = state;
  const { activeSeasons, list, sortedSeasonKeys } = state.seasons;

  return {
    activeSeasons,
    activeSeason: activeSeasons[prop.index],
    activeMapLayer: layers.activeMapLayers[prop.index],
    disabledMapLayers: layers.disabledMapLayers,
    fieldId: field.id,
    isSplitViewEnabled: layers.isSplitViewEnabled,
    isSplitViewButtonVisible: !layers.isSplitViewEnabled,
    isWeatherVisible: state.map.isWeatherVisible,
    layers,
    seasons: list,
    sortedSeasonKeys,
  };
}

const mapDispatchToProps = {
  trackAnalyticsEvent: trackEvent,
  trackAnalyticsHeapEvent: trackHeapEvent
};

export default connect(mapStateToProps, mapDispatchToProps)(Selectors);
